import type { Ref } from 'vue';
import type { LocationQuery } from 'vue-router';
import type * as openapi from '@/helpers/api/types/openapiOverrides';

import { ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import _isEqual from 'lodash/isEqual';

import { fetchGetNews } from '@/helpers/fetchers';
import * as routeNames from '@/constants/routeNames';

export interface HomePageViewComposable {
  isFetchingData: Ref<boolean>;
  serviceUpdates: Ref<openapi.NewsListContentOpenapi>;
  serviceUpdatesLimit: Ref<number>;
  newsLimited: Ref<openapi.NewsListContentOpenapi>;
  limit: Ref<number>;
  handleLimitClick: (limitValue: number) => void;
  handleServiceUpdatesLimitClick: (serviceUpdatesLimitValue: number) => void;
}

export default function useHomePageView(): HomePageViewComposable {
  const route = useRoute();

  const limit = ref(2);
  const serviceUpdatesLimit = ref(2);

  const isFetchingData = ref(false);
  const fetchedData = ref<openapi.NewsListContentOpenapi>();

  const newsLimited = ref<openapi.NewsListContentOpenapi>([]);
  const serviceUpdates = ref<openapi.NewsListContentOpenapi>([]);

  function handleLimitClick(limitValue: number): void {
    limit.value = limitValue;
    updateNewsLimited();
  }

  function handleServiceUpdatesLimitClick(
    serviceUpdatesLimitValue: number,
  ): void {
    serviceUpdatesLimit.value = serviceUpdatesLimitValue;
    updateServiceUpdates();
  }

  async function fetchData(newQuery: LocationQuery): Promise<void> {
    fetchedData.value = undefined;
    isFetchingData.value = true;
    await fetchGetNews()
      .then((data) => {
        if (!_isEqual(route.query, newQuery)) {
          return;
        }
        fetchedData.value = data;
      })
      .finally(() => {
        isFetchingData.value = false;
      });
  }

  function updateNewsLimited(): void {
    if (!fetchedData.value) return;
    newsLimited.value = fetchedData.value.filter(
      (item) => item.type !== 'service',
    );
    if (limit.value) {
      newsLimited.value = newsLimited.value.slice(0, limit.value);
    }
  }

  function updateServiceUpdates(): void {
    if (!fetchedData.value) return;
    serviceUpdates.value = fetchedData.value.filter(
      (item) => item.type === 'service',
    );
    if (serviceUpdatesLimit.value) {
      serviceUpdates.value = serviceUpdates.value.slice(
        0,
        serviceUpdatesLimit.value,
      );
    }
  }

  function updateResults(): void {
    if (!fetchedData.value) {
      newsLimited.value = [];
      serviceUpdates.value = [];
      return;
    }
    updateNewsLimited();
    updateServiceUpdates();
  }

  watch(
    route,
    async (newRoute) => {
      if (newRoute.name === routeNames.HOME) {
        await fetchData(newRoute.query);
        updateResults();
      }
    },
    { immediate: true },
  );

  return {
    isFetchingData,
    serviceUpdates,
    serviceUpdatesLimit,
    newsLimited,
    limit,
    handleLimitClick,
    handleServiceUpdatesLimitClick,
  };
}
